import { useEffect, useRef, useState } from 'react';

import { CloseButton } from '@/components/common/close-button';

import styles from './image-lightbox.module.scss';

export type LightboxImage = {
  id: string;
  url: string;
  /** The image's accessible name, on both the full-size view and its filmstrip tile. */
  alt: string;
};

export type ImageLightboxProps = {
  images: readonly LightboxImage[];
  /** Which image the lightbox opens on — the thumbnail the Grower selected. */
  initialIndex?: number;
  onClose: () => void;
};

// The dialog's accessible name and the close control's label. Not a prop: every caller wants the
// same two strings, and a `label` prop deriving `closeLabel` from it was one possible input
// dressed up as configurability (YAGNI) — zero callers ever passed anything else.
const DIALOG_LABEL = 'Image preview';
const CLOSE_LABEL = 'Close image preview';

/**
 * One evidence image full-size and centred over the whole page, with a filmstrip of the claim's
 * other images beneath it (Figma `1158:607446`; grilling Q8).
 *
 * **The consumer owns mounting** — render it conditionally, which is what seeds `initialIndex` on
 * each open and what makes the index uncontrolled. The consumer also owns returning focus to the
 * thumbnail that opened it: it is the only one holding that element.
 *
 * This is rendered **inside the Ticket Preview drawer's tree, not through a portal**, which is what
 * keeps antd's focus lock from stealing focus back. See plan.md § Layering — including why the
 * drawer must stop listening for Esc while this is open.
 */
export function ImageLightbox({ images, initialIndex = 0, onClose }: ImageLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const dialogRef = useRef<HTMLDivElement>(null);

  // AC: focus moves into the lightbox when it opens. Mount-only on purpose — the component is
  // mounted per open, so this must not re-fire when a prop identity changes underneath it (Task
  // 3's consumer passes an inline `onClose` arrow, which gets a fresh identity on every parent
  // re-render, e.g. a TanStack Query refetch-on-window-focus; if this effect depended on `onClose`
  // it would yank focus off whatever the Grower is interacting with, such as a filmstrip tile).
  // Focusing the dialog element itself — rather than a control inside it — is antd's own drawer
  // behaviour.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // Esc, the arrow keys and the Tab trap. Attached imperatively (`addEventListener`), not as a
  // JSX `onKeyDown` prop: `jsx-a11y/no-noninteractive-element-interactions` treats `role="dialog"`
  // as a non-interactive role and flags *any* JSX interactive-event prop on it (keyboard included,
  // not just click) — the same pattern `map-anchored-panel.tsx`'s focus trap already uses for this
  // reason. This effect may legitimately depend on `onClose` and re-attach when it changes
  // identity; unlike the focus-on-open effect above, re-running this one has no user-visible
  // effect (the listener is just swapped for an equivalent one).
  //
  // The trap is required, not optional: `aria-modal="true"` promises assistive tech that nothing
  // outside this dialog is reachable, and the consumer only makes good on that for its own panel
  // body via `inert` — `<OverlayChrome>`'s header close control sits outside that `inert` region
  // and stays tabbable, so without a trap here Shift+Tab would escape the lightbox entirely. Same
  // focusable-selector query and wrap-around logic as `map-anchored-panel.tsx`'s `trapFocus`,
  // scoped to this element so the trap dies with the dialog. The extra `tabIndex !== -1` filter
  // (that file doesn't need it) excludes the scrim: it is a real `<button>`, so the plain selector
  // alone would still catch it despite its `tabIndex={-1}`.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Stop here, or this same keypress also closes the Ticket Preview behind the lightbox.
        // The drawer's Esc handler is a **bubble-phase** `window` listener
        // (`@rc-component/portal`'s `useEscKeyDown`), so it sits above this element in the
        // propagation path and `stopPropagation` genuinely pre-empts it.
        //
        // The consumer's `closeOnEsc={!lightboxOpen}` cannot do this job on its own: a browser
        // runs a microtask checkpoint between two listeners on one bubble path, so React flushes
        // the `onClose()` below and re-renders — turning `keyboard` back to `true`, and
        // `useEvent` refreshes the drawer's handler during that render — all before `window` is
        // reached. That prop stays as a second layer for the case where focus is somehow outside
        // this dialog and the keypress never reaches us at all.
        event.stopPropagation();
        onClose();
        return;
      }
      // Navigation stops at the ends rather than wrapping (AC). With a single image both clamps
      // are no-ops, which is the whole of "there is simply nothing to move between".
      if (event.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (event.key === 'ArrowRight') setIndex((i) => Math.min(images.length - 1, i + 1));

      if (event.key === 'Tab') {
        const stops = [
          ...dialog.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ].filter((element) => element.tabIndex !== -1);
        const first = stops[0];
        const last = stops[stops.length - 1];
        if (!first || !last) {
          event.preventDefault();
          dialog.focus();
          return;
        }
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    dialog.addEventListener('keydown', onKeyDown);
    return () => dialog.removeEventListener('keydown', onKeyDown);
  }, [images.length, onClose]);

  const current = images[index];
  if (!current) return null;

  return (
    <div
      ref={dialogRef}
      className={styles.mask}
      role="dialog"
      aria-modal="true"
      aria-label={DIALOG_LABEL}
      tabIndex={-1}
    >
      {/* "A click outside the image" (AC). It has to be a real interactive element:
          `jsx-a11y/no-noninteractive-element-interactions` treats `role="dialog"` as
          non-interactive and flags any JSX event prop on it, so an `onClick` on the element above
          is a lint error (see the mount effect above for the same reason arrow/Esc handling is
          attached imperatively instead of via `onKeyDown`). `aria-hidden` + `tabIndex={-1}` keep
          this a click target rather than a second control — the labelled round button below is
          the one a screen reader announces. The card and the filmstrip are lifted above it in the
          SCSS, so a click on either never dismisses. */}
      <button
        type="button"
        className={styles.scrim}
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
      />

      <div className={styles.card}>
        <img className={styles.image} src={current.url} alt={current.alt} />
        <CloseButton
          tone="dark"
          className={styles.close}
          aria-label={CLOSE_LABEL}
          onClick={onClose}
        />
      </div>

      {/* No filmstrip for a lone image: Figma's five tiles are sample data, and a strip of one
          marks an image the Grower is already looking at. */}
      {images.length > 1 ? (
        <div className={styles.filmstrip}>
          {images.map((image, i) => (
            <button
              key={image.id}
              type="button"
              className={i === index ? `${styles.thumb} ${styles.thumbActive}` : styles.thumb}
              // The one being viewed, marked for a screen reader as well as by the green outline.
              aria-current={i === index ? true : undefined}
              onClick={() => setIndex(i)}
            >
              <img className={styles.thumbImg} src={image.url} alt={image.alt} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
